import {isEmpty, sortBy} from "lodash"
import {
    ExternalCalendarProvider,
    IntegrationAccount,
    IntegrationAccountCalendar,
    LinkedCalendarInfo
} from "types/calendar"

export type ConnectedAccountGroup = {
    key: string
    accountId: number | null
    name: string
    provider: ExternalCalendarProvider
    requiresReconnect: boolean
    /** Account details from calendars-integration, null when they couldn't be loaded */
    account: IntegrationAccount | null
    /** Synced calendars that already exist in Edular */
    calendars: LinkedCalendarInfo[]
    /** Synced calendars that Edular hasn't created yet (first sync in progress) */
    pendingCalendars: IntegrationAccountCalendar[]
    /** Provider calendars that can be synced but aren't */
    availableCount: number
}

const getGroupKey = (accountId: number | null | undefined, name: string) =>
    accountId != null ? `id:${accountId}` : `name:${name}`

/**
 * Groups integration calendars by their linked Google/Outlook account.
 * Works without `accounts` (e.g. calendars-integration is unavailable) by grouping on the
 * account data Edular already has for each calendar.
 */
export const groupConnectedCalendars = (
    linkedCalendars: LinkedCalendarInfo[],
    accounts?: IntegrationAccount[] | null
): ConnectedAccountGroup[] => {
    const groups = new Map<string, ConnectedAccountGroup>()

    for (const account of accounts ?? []) {
        groups.set(getGroupKey(account.id, account.name), {
            key: getGroupKey(account.id, account.name),
            accountId: account.id,
            name: account.name,
            provider: account.provider,
            requiresReconnect: account.requiresReconnect,
            account,
            calendars: [],
            pendingCalendars: [],
            availableCount: account.calendars.filter((calendar) => !calendar.synced && calendar.canSync).length
        })
    }

    for (const calendar of linkedCalendars) {
        if (!calendar.provider || calendar.isPrimary) {
            continue
        }

        const accountName = calendar.accountName ?? calendar.name
        const key = getGroupKey(calendar.accountId, accountName)
        let group = groups.get(key)

        if (!group) {
            group = {
                key,
                accountId: calendar.accountId ?? null,
                name: accountName,
                provider: calendar.provider,
                requiresReconnect: false,
                account: null,
                calendars: [],
                pendingCalendars: [],
                availableCount: 0
            }
            groups.set(key, group)
        }

        // A calendar the user just stopped syncing, which Edular hasn't removed yet
        const isUnsynced =
            group.account && !group.account.calendars.some((item) => item.synced && item.id === calendar.id)

        if (!isUnsynced) {
            group.calendars.push(calendar)
        }

        group.requiresReconnect = group.requiresReconnect || calendar.requiresReconnect
    }

    for (const group of groups.values()) {
        if (!group.account) {
            continue
        }
        const edularCalendarIds = new Set(group.calendars.map((calendar) => calendar.id))
        group.pendingCalendars = group.account.calendars.filter(
            (calendar) => calendar.synced && calendar.id != null && !edularCalendarIds.has(calendar.id)
        )
    }

    return sortBy(
        [...groups.values()].filter((group) => group.calendars.length || group.pendingCalendars.length),
        (group) => group.name.toLowerCase()
    )
}

/**
 * Applies the saved "show" selection. Calendars that weren't in the previous list
 * (`knownCalendarIds`) are shown, so a newly synced calendar isn't hidden by an older selection.
 */
export const applyCalendarSelection = (
    calendars: LinkedCalendarInfo[],
    selectedCalendarIds: number[] | null | undefined,
    knownCalendarIds: Set<number> | null
): {calendars: LinkedCalendarInfo[]; hasNewCalendars: boolean} => {
    // First time / unset: show all calendars checked
    if (isEmpty(selectedCalendarIds)) {
        return {calendars: calendars.map((calendar) => ({...calendar, isShow: true})), hasNewCalendars: false}
    }

    const selectedSet = new Set(selectedCalendarIds.map(Number))
    let hasNewCalendars = false

    const result = calendars.map((calendar) => {
        const isNew = !!knownCalendarIds && !knownCalendarIds.has(calendar.calendarId)
        hasNewCalendars = hasNewCalendars || isNew
        return {...calendar, isShow: isNew || selectedSet.has(calendar.calendarId)}
    })

    return {calendars: result, hasNewCalendars}
}

/**
 * Integration events are stored with a default color; show them in their calendar's provider color
 */
export const applyIntegrationCalendarColors = <T extends {calendar_id?: number; json_data?: any}>(
    events: T[],
    linkedCalendars: LinkedCalendarInfo[]
): T[] => {
    const colors = new Map(
        linkedCalendars
            .filter((calendar) => calendar.provider && calendar.color)
            .map((calendar) => [calendar.calendarId, calendar.color])
    )

    if (!colors.size) {
        return events
    }

    return (events || []).map((event) =>
        event?.json_data?.isFromIntegration && colors.has(event.calendar_id)
            ? {...event, json_data: {...event.json_data, color: colors.get(event.calendar_id)}}
            : event
    )
}

export type CalendarConnectResult = "ok" | "cancelled" | "consented" | "error"

export type CalendarConnectReturn = {
    provider: ExternalCalendarProvider
    result: CalendarConnectResult
    error: string | null
}

export type CalendarConnectNotice = {
    type: "success" | "info" | "error"
    messageKey: string
}

/** Query params calendars-integration adds to `successRedirect` after the provider sign-in */
export const CALENDAR_CONNECT_PARAMS = ["connected", "result", "error"]

const CALENDAR_CONNECT_RESULTS: CalendarConnectResult[] = ["ok", "cancelled", "consented", "error"]

export const CALENDAR_PROVIDER_LABELS: Record<ExternalCalendarProvider, string> = {
    [ExternalCalendarProvider.Google]: "Google",
    [ExternalCalendarProvider.Outlook]: "Outlook"
}

/**
 * Reads the provider sign-in result from the URL search string.
 * Returns null when the URL isn't a return from the sign-in or has an unknown provider/result.
 */
export const parseCalendarConnectResult = (search: string): CalendarConnectReturn | null => {
    const params = new URLSearchParams(search)
    const provider = params.get("connected") as ExternalCalendarProvider
    const result = params.get("result") as CalendarConnectResult

    if (!Object.values(ExternalCalendarProvider).includes(provider) || !CALENDAR_CONNECT_RESULTS.includes(result)) {
        return null
    }

    return {provider, result, error: result === "error" ? params.get("error") || null : null}
}

/** Removes the sign-in result params from an absolute URL, keeping other params and the hash */
export const stripCalendarConnectParams = (url: string): string => {
    const parsedUrl = new URL(url)

    if (!CALENDAR_CONNECT_PARAMS.some((param) => parsedUrl.searchParams.has(param))) {
        return url
    }

    CALENDAR_CONNECT_PARAMS.forEach((param) => parsedUrl.searchParams.delete(param))
    parsedUrl.search = parsedUrl.searchParams.toString()
    return parsedUrl.toString()
}

/** Picks the message shown to the user after returning from the provider sign-in */
export const getCalendarConnectNotice = ({result, error}: CalendarConnectReturn): CalendarConnectNotice => {
    switch (result) {
        case "ok":
            return {type: "success", messageKey: "calendar.connectedCalendars.connectSuccess"}
        case "consented":
            return {type: "success", messageKey: "calendar.connectedCalendars.connectConsented"}
        case "cancelled":
            return {type: "info", messageKey: "calendar.connectedCalendars.connectCancelled"}
        default:
            if (error === "admin_consent_required") {
                return {type: "error", messageKey: "calendar.connectedCalendars.connectAdminConsentRequired"}
            }
            if (error === "no_calendar") {
                return {type: "error", messageKey: "calendar.connectedCalendars.connectNoCalendar"}
            }
            return {type: "error", messageKey: "calendar.connectedCalendars.connectFailed"}
    }
}
